import { Component, Inject, Input, OnChanges, OnInit } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { ITealiumAnalyticsOptions, TealiumAnalyticsEvent, TealiumAnalyticsService, TealiumConstants } from '@ddt-web/analytics';
import { APPLICATION_NAME_TOKEN, ApplicationName, BenchmarkOption, ViewCategory } from '@ddt-web/config';
import { ConfigService, InjectConfig, PaConfigKey, SharedConfigKey } from '@ddt-web/config-layer';
import { CellData, CellRow, CellUtil, ColumnText, ComponentData, DataServiceApiConstants, PortfolioDataUtil } from '@ddt-web/data-service';
import { ApplicationNames } from '@ddt-web/domain';
import { StringUtil } from '@ddt-web/generic-utils';
import { PerformanceSection, PerformanceUtil } from '@ddt-web/performance';
import { ApplicationStateStoreProxyService, PerformanceState } from '@ddt-web/shared-stores';
import { UrlService } from '@ddt-web/url';
import { PartnerUserProfileService } from '@ddt-web/user-data';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as get from 'lodash/get';
import * as isArray from 'lodash/isArray';
import * as isEmpty from 'lodash/isEmpty';
import * as isNil from 'lodash/isNil';
import { distinctUntilKeyChanged, first } from 'rxjs/operators';

import { DynamicHoldingsAnalyticsHeadings, DynamicHoldingsAnalyticsInfoBubbles, HoldingsConstants } from '../../config/holdings.constants';
import { OverviewColumn } from '../../interfaces/overview-column.interface';

@UntilDestroy()
@Component({
	selector: 'hl-holdings-analytics',
	templateUrl: './holdings-analytics.component.html',
	styleUrls: ['./holdings-analytics.component.scss']
})
export class HoldingsAnalyticsComponent implements OnChanges, OnInit {

	@InjectConfig(PaConfigKey.ENABLE_PROPOSAL_VIEW) public readonly showExpectedShortfall: boolean = false;

	@Input() public data: ComponentData;
	@Input() public performanceTitle: string;
	@Input() public customBenchmark?: string;
	@Input() public hideCost: boolean = false;
	@Input() public hideCommonPeriod: boolean = false;

	public isCustomFeeEnabled: boolean = false; // decides whether we show the option to set custom fee (config-driven: enabled in PA, disabled in Nile)
	public selectedBenchmarkForCaptureRatio: string;
	public isNetPerformanceValid: boolean = true;

	public snapshotDataIds = HoldingsConstants.SNAPSHOT_DATA_IDS;
	public benchmarkOptions = BenchmarkOption;

	// columns array per section with default values
	public performanceColumns: Array<string | PerformanceSection> = HoldingsConstants.PERFORMANCE_COLUMNS;
	public performanceColumnsAsOfDateMapping: { [key: string]: string } = {
		performanceReturn: 'asOfDatePerformance',
		twelveMonthTrailingYield: 'asOfDateDetails',
		performanceDownsideUpside: 'asOfDatePerformance',
		performanceDownsideUpsideBenchmark: 'asOfDatePerformance',
		netCustomPerformanceReturn: 'asOfDatePerformance',
		netCustomPerformanceDownsideUpside: 'asOfDatePerformance',
		netCustomPerformanceDownsideUpsideBenchmark: 'asOfDatePerformance'
	};
	public riskColumns: Array<OverviewColumn> = [
		{ columnId: 'historicalRisk', asOfDateId: 'asOfDatePerformance' },
		{ columnId: 'totalRisk', asOfDateId: 'asOfDateRisk' }
	];

	public costColumns: Array<string> = ['expenseRatio', 'taxCost'];

	public performanceTitleCells: { [key: string]: CellData } = {};
	public customAdvisoryFee: number;

	public analysisSectionClass: string;
	public riskAndCostSectionClass: string;
	public riskAndCostIndividualClass: string;
	public categoryContainerClass: string;
	public dataContainerClass: string;
	public dataItemClass: string;
	public riskInfoBubbleText: string;
	public asOfDates: any;
	public cellUtils = CellUtil;
	public portfolioIds: any[] = [];
	public hasMultiPortfolios: boolean;
	public showExpenseRatioAsOfDate: boolean;
	public showRiskInfoBubbleModal: boolean = false;
	public isProposalMetricsSectionCollapsed: boolean = true; // by default the proposal metrics should be collapsed
	public hasProposalMetrics: boolean = false;
	public ViewCategory = ViewCategory;
	public isCommonPeriodModalVisible: boolean = false;
	public numberOfPortfolios: number;
	public hasBenchmarkPortfolio: boolean = false;
	public hasAcBenchmark: boolean = false;
	public displayISOCurrency: string;

	constructor(
		private tealiumAnalyticsService: TealiumAnalyticsService,
		private urlService: UrlService,
		public partnerUserProfileService: PartnerUserProfileService,
		public applicationStateStoreProxyService: ApplicationStateStoreProxyService,
		private contentService: ContentService,
		private configService: ConfigService,
		@Inject(APPLICATION_NAME_TOKEN) private applicationName: ApplicationName
	) {
		this.isCustomFeeEnabled = this.configService.getBoolean(SharedConfigKey.EnableCustomFee, false);
		this.hasAcBenchmark = !isEmpty(this.urlService.getAcBenchmark());
	}

	public ngOnInit(): void {
		this.applicationStateStoreProxyService.getSelectedCurrency()
			.pipe(first(currency => !!currency))
			.subscribe(currency => {
				this.displayISOCurrency = currency;
			});
	}

	public ngOnChanges(): void {
		if (this.data.success) {
			if (this.showExpectedShortfall) {
				this.riskColumns.push({ columnId: 'expectedShortfall', asOfDateId: 'asOfDateRisk' });
			}
			this.hasBenchmarkPortfolio = this.hasAcBenchmark || PortfolioDataUtil.hasBenchmarkPortfolio(this.data.portfolioData);

			this.applicationStateStoreProxyService.getProposalState().pipe(
				untilDestroyed(this),
				distinctUntilKeyChanged('metrics', (previousMetrics, currentMetrics) => {
					return JSON.stringify(previousMetrics) === JSON.stringify(currentMetrics);
				})
			).subscribe(state => {
				this.hasProposalMetrics = !isEmpty(state.metrics);
			});

			this.applicationStateStoreProxyService.getPerformanceState()
				.pipe(untilDestroyed(this))
				.subscribe((state: PerformanceState) => {
					this.customAdvisoryFee = state.customAdvisoryFee;
					if (this.hasBenchmarkPortfolio) {
						this.selectedBenchmarkForCaptureRatio = state.selectedBenchmarkForCaptureRatio || BenchmarkOption.BENCHMARK;
					}
					// update tabs to show
					this.updateColumns();

					// validate tabs when customAdvisoryFee is set
					if (!isNil(this.customAdvisoryFee)) {
						this.validateNetColumns();
					}
				});

			this.hasMultiPortfolios = this.data.tabs[0].tableData[0].headersY.length > 1;
			this.assignColumnClasses(this.data.portfolioData);
			this.portfolioIds = this.data.portfolioData.map(portfolio => get(portfolio, 'field.id.formattedValue'));
			this.numberOfPortfolios = this.portfolioIds.length;
			this.asOfDates = this.data.tabs.map(tab => {
				const performanceDate = CellUtil.getById(tab.tableData[0].cells[0], 'asOfDatePerformance');
				const riskDate = CellUtil.getById(tab.tableData[0].cells[0], 'asOfDateRisk');
				const detailsDate = CellUtil.getById(tab.tableData[0].cells[0], 'asOfDateDetails');

				return {
					id: tab.config.id,
					asOfDateCost: get(this.data, 'portfolioData[0].field["asOfDate"].formattedValue'),
					asOfDatePerformance: performanceDate ? performanceDate.formattedValue : null,
					asOfDateRisk: riskDate ? riskDate.formattedValue : null,
					asOfDateDetails: detailsDate ? detailsDate.formattedValue : null
				};
			});

		}
	}

	public onBenchmarkOptionChange(benchmarkOption?: string): void {
		this.applicationStateStoreProxyService.updateSelectedBenchmarkForCaptureRatio(
			{ selectedBenchmarkForCaptureRatio: this.selectedBenchmarkForCaptureRatio || benchmarkOption }
		);
	}

	public onProposalAccordionClicked(): void {
		this.isProposalMetricsSectionCollapsed = !this.isProposalMetricsSectionCollapsed;
		const tealiumTrackingEvent = new TealiumAnalyticsEvent(
			'proposal-snapshot-accordion:cta:toggle',
			null
		);
		this.tealiumAnalyticsService.trackEventTealium(tealiumTrackingEvent);
	}

	/**
	 * For each tab, it goes through each column section and each children (eg performanceReturn and netCustomPerformanceReturn)
	 * and checks there's at least one valid value for at least 1 portfolio.
	 */
	private validateNetColumns(): void {
		this.isNetPerformanceValid = this.data.tabs.map(tab => {
			return this.performanceColumns.filter(columnSection => !isEmpty((columnSection as PerformanceSection)?.children)).map(columnSection => {
				return tab.tableData[0].cells.some(cellData => { // check if some portfolio data is valid
					return (columnSection as PerformanceSection).children.every(childColumnId => { // check if both gross and net values are valid
						const rawValue = cellData.find(cell => cell.config.id === childColumnId)?.rawValue;
						// upside/downside fields are array values
						return isArray(rawValue) ? (rawValue as Array<any>).some(rawVal => !isEmpty(rawVal)) : !isNil(rawValue);
					});
				});
			}).some(isCellValid => !!isCellValid);
		}).some(isCellValid => !!isCellValid);
	}

	private updateColumns(): void {
		this.performanceColumns = this.hasBenchmarkPortfolio && this.selectedBenchmarkForCaptureRatio === BenchmarkOption.BENCHMARK ?
			HoldingsConstants.PERFORMANCE_COLUMNS_BENCHMARK : HoldingsConstants.PERFORMANCE_COLUMNS;

		if (this.isCustomFeeEnabled) {
			if (this.hasBenchmarkPortfolio && this.selectedBenchmarkForCaptureRatio === BenchmarkOption.BENCHMARK) {
				this.performanceColumns = this.customAdvisoryFee ?
					HoldingsConstants.PERFORMANCE_COLUMNS_WITH_NET_CUSTOM_BENCHMARK :
					HoldingsConstants.PERFORMANCE_COLUMNS_NO_NET_BENCHMARK;
			} else {
				this.performanceColumns = this.customAdvisoryFee ? HoldingsConstants.PERFORMANCE_COLUMNS_WITH_NET_CUSTOM : HoldingsConstants.PERFORMANCE_COLUMNS_NO_NET;
			}
			this.performanceTitleCells = PerformanceUtil.getSectionTitleCells(this.performanceColumns, this.contentService);
			this.data.tabs.forEach(tab => PerformanceUtil.updateSnapshotPerformanceCells(tab, this.contentService, this.customAdvisoryFee));
		}
	}

	private assignColumnClasses(portfolios: CellRow[]): void {
		switch (portfolios.length) {
			case 1:
				if (this.isCustomFeeEnabled) {
					this.analysisSectionClass = 'col-12 col-sm-24';
					this.riskAndCostSectionClass = 'col-12 col-sm-24';
					this.riskAndCostIndividualClass = 'col-24 col-sm-24 partner';
				} else {
					this.analysisSectionClass = 'col-8 col-sm-24';
					this.riskAndCostSectionClass = 'col-16 col-sm-24';
					this.riskAndCostIndividualClass = 'col-12 col-sm-24';
				}
				this.categoryContainerClass = '';
				this.dataContainerClass = '';
				this.dataItemClass = '';
				break;
			case 2:
				this.analysisSectionClass = 'col-24 col-sm-24';
				this.riskAndCostSectionClass = 'col-24 col-sm-24';
				this.riskAndCostIndividualClass = 'col-24 col-sm-24';
				if (this.applicationName === ApplicationNames.PORTFOLIO_ANALYSIS_CONSTRUCTION) {
					this.dataContainerClass = 'col-18 col-sm-24';
					this.categoryContainerClass = 'col-6 col-sm-24';
					this.dataItemClass = 'col-12 col-sm-24 left-padding';
				} else {
					this.dataContainerClass = 'col-15 col-sm-24';
					this.categoryContainerClass = 'col-9 col-sm-24';
					this.dataItemClass = this.urlService.hasSpecificPortfolioId(DataServiceApiConstants.FTE_TRANSITION_ID)
						? 'col-12 col-sm-24 more-portfolios'
						: 'col-8 col-sm-24 more-portfolios';
				}
				break;
			case 3:
				this.analysisSectionClass = 'col-24 col-sm-24';
				this.riskAndCostSectionClass = 'col-24 col-sm-24';
				this.riskAndCostIndividualClass = 'col-24 col-sm-24';
				if (this.applicationName === ApplicationNames.PORTFOLIO_ANALYSIS_CONSTRUCTION) {
					this.categoryContainerClass = 'col-6 col-sm-24';
					this.dataContainerClass = 'col-18 col-sm-24';
					this.dataItemClass = 'col-8 col-sm-24';
				} else if (this.applicationName === ApplicationNames.PORTFOLIO_CENTRE_360) {
					this.categoryContainerClass = 'col-6 col-sm-24';
					this.dataContainerClass = 'col-18 col-sm-24';
					this.dataItemClass = 'col-6 col-sm-24';
				} else {
					this.categoryContainerClass = 'col-9 col-sm-24';
					this.dataContainerClass = 'col-15 col-sm-24';
					this.dataItemClass = this.urlService.hasSpecificPortfolioId(DataServiceApiConstants.FTE_TRANSITION_ID)
						? 'col-12 col-sm-24 more-portfolios'
						: 'col-8 col-sm-24 more-portfolios';
				}
				break;
			case 4:
				this.analysisSectionClass = 'col-24 col-sm-24';
				this.riskAndCostSectionClass = 'col-24 col-sm-24';
				this.riskAndCostIndividualClass = 'col-24 col-sm-24';
				this.dataContainerClass = 'col-18 col-sm-24';
				this.dataItemClass = 'col-6 col-sm-24';
				if (this.applicationName === ApplicationNames.PORTFOLIO_ANALYSIS_CONSTRUCTION || this.applicationName === ApplicationNames.PORTFOLIO_CENTRE_360) {
					this.categoryContainerClass = 'col-6 col-sm-24';
				} else {
					this.categoryContainerClass = 'col-5 col-sm-24';
				}
				break;
			case 5:
				this.analysisSectionClass = 'col-24 col-sm-24';
				this.riskAndCostSectionClass = 'col-24 col-sm-24';
				this.riskAndCostIndividualClass = 'col-24 col-sm-24';
				if (this.applicationName === ApplicationNames.PORTFOLIO_ANALYSIS_CONSTRUCTION) {
					this.categoryContainerClass = 'col-4 col-sm-24';
					this.dataContainerClass = 'col-20 col-sm-24';
					this.dataItemClass = 'col-4 col-sm-24 padding-5-override';
				} else {
					this.categoryContainerClass = 'col-3 col-sm-24';
					this.dataContainerClass = 'col-18 col-sm-24';
					this.dataItemClass = 'col-4 col-sm-24';
				}
				break;
			case 6:
				this.analysisSectionClass = 'col-24 col-sm-24';
				this.riskAndCostSectionClass = 'col-24 col-sm-24';
				this.riskAndCostIndividualClass = 'col-24 col-sm-24';
				this.categoryContainerClass = 'col-4 col-sm-24';
				this.dataContainerClass = 'col-20 col-sm-24';
				this.dataItemClass = 'col-4 col-sm-24 padding-6-7-override';
				break;
			case 7:
				this.analysisSectionClass = 'col-24 col-sm-24';
				this.riskAndCostSectionClass = 'col-24 col-sm-24';
				this.riskAndCostIndividualClass = 'col-24 col-sm-24';
				this.categoryContainerClass = 'col-4 col-sm-24';
				this.dataContainerClass = 'col-20 col-sm-24';
				this.dataItemClass = 'col-3 col-sm-24 padding-6-7-override';
				break;
		}
	}

	public toggleInfoDialog(id: any, tabIndex: number): void {
		const analyticData: ITealiumAnalyticsOptions = { linkName: 'tooltip_' + id };
		const event = new TealiumAnalyticsEvent(TealiumConstants.TOOLS_EVENT.TOOL_TIP, analyticData);
		this.tealiumAnalyticsService.trackEventTealium(event);
		const infoBubbleCell = CellUtil.getById(this.data.tabs[tabIndex].tableData[0].cells[0], this.snapshotDataIds.TOTAL_RISK);
		this.riskInfoBubbleText = get(infoBubbleCell, 'config.infoBubble.translated', '').toString();
		this.showRiskInfoBubbleModal = !this.showRiskInfoBubbleModal;
	}

	public replacePlaceholders(textToFormat: ColumnText): string {
		const templates: string[] = [...Object.values(DynamicHoldingsAnalyticsHeadings), ...Object.values(DynamicHoldingsAnalyticsInfoBubbles)];
		if (this.customBenchmark && templates.includes(textToFormat.translated)) {

			return StringUtil.fillTemplate(
				textToFormat.translated,
				{ customBenchmark: this.customBenchmark }
			);
		} else {
			return textToFormat.translated;
		}
	}
}
